import { useUpdateTaskTypeMutation } from '@/store/api/services/operations/taskTypes';
import type { TaskType } from '@/types/serviceTicket';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

interface EditTaskTypeProps extends ModalChildrenProps {
  props: {
    taskType: TaskType;
  } | null;
}

const EditTaskTypeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
  is_active: z.boolean(),
});

type EditTaskTypeSchemaType = z.infer<typeof EditTaskTypeSchema>;

const EditTaskType = ({ hide, props }: EditTaskTypeProps) => {
  const taskType = props?.taskType;

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EditTaskTypeSchemaType>({
    resolver: zodResolver(EditTaskTypeSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: taskType?.name || '',
      description: taskType?.description || '',
      is_active: taskType?.is_active ?? true,
    },
  });

  const [updateTaskType] = useUpdateTaskTypeMutation();

  const onSubmit = async (payload: EditTaskTypeSchemaType) => {
    if (!taskType?.id) {
      toast.error('Task type ID is missing');
      return;
    }

    try {
      await updateTaskType({ id: taskType.id, ...payload }).unwrap();
      toast.success('Task type updated successfully');
      hide('edit-task-type');
    } catch (error: any) {
      const errorMessage =
        error?.meta?.message ||
        error?.data?.message ||
        error?.message ||
        'Failed to update task type';
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 3 }}>
        <Stack gap={2}>
          <TextField
            label="Name *"
            fullWidth
            error={!!errors.name}
            helperText={errors?.name?.message}
            size="small"
            {...register('name')}
          />

          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="Active"
              />
            )}
          />

          <Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Description *
            </Typography>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <>
                  <TextareaAutosize
                    {...field}
                    minRows={4}
                    maxRows={8}
                    placeholder="Provide a detailed description of the task type"
                    style={{
                      width: '100%',
                      resize: 'vertical',
                      padding: '12px 14px',
                      border: errors.description
                        ? '1px solid #d32f2f'
                        : '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      lineHeight: '1.5',
                      backgroundColor: 'transparent',
                      outline: 'none',
                      cursor: 'text',
                    }}
                  />
                  {errors.description && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.description.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => hide('edit-task-type')}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          {isSubmitting ? 'Updating...' : 'Update Task Type'}
        </Button>
      </DialogActions>
    </>
  );
};

export default EditTaskType;
